/* eslint-disable */
import React, { useContext, FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Box, FormControlLabel, IconButton, Menu, MenuItem, Switch, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';

// OL
import { Extent } from 'ol/extent';

// CUSTOM COMPONENTS
import ModelTable from '@/ui/Table/ModelTable';
import DataController from '@/lib/DataController';
import modelComments from '@/models/comments';
import MapContext from '@/context/MapContext/MapContext';
import DialogContext from '@/context/DialogContext/DialogContext';
import TableButton from '@/ui/Table/ActionButtons/TableButton';
import CommentShapeDialog from '@/views/Comments/CommentShapeDialog';
import OlSourceVector from 'ol/source/Vector';
import OlFeature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';

// TYPES
import { DrawingType } from '@/@types/common/index';
import { DCRecord } from '@/@types/lib/dataController';

export interface ICommentsPane {
  changeDrawType: (type: DrawingType) => void;
  drawType: DrawingType | undefined;
  records: DCRecord[];
  visible?: boolean;
  toggleVisibility?: () => void;
  setSelectedSource: React.Dispatch<React.SetStateAction<OlSourceVector<OlFeature<Geometry>>>>
}

const CommentsPane: FunctionComponent<ICommentsPane> = (props) => {
  const { changeDrawType, drawType, records, visible, toggleVisibility, setSelectedSource } =
    props;

  const { t } = useTranslation();
  const dc = new DataController(modelComments);
  const mapContext = useContext(MapContext);
  const dialogContext = useContext(DialogContext);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);

  const filteredRecords = useMemo(() => {
    if (!showActiveOnly) return records;
    return records.filter((record) => record.active !== false);
  }, [records, showActiveOnly]);

  const handleAddComment = () => {
    dialogContext.showDialog(CommentShapeDialog, {
      drawType,
      changeDrawType,
    });
  };

  function handleRowAction(action: string, id: number, rowRecord?: DCRecord) {
    const record = rowRecord ?? filteredRecords.find((x: DCRecord) => x.id === id);
    if (!record) return;

    switch (action) {
      case 'show_on_map':
        handleShowOnMap(record as DCRecord);
        break;
      default:
        break;
    }
  }

  function handleShowOnMap(record?: DCRecord) {
    if (mapContext?.map) {
      if(record) {
        const feature = record.geom as OlFeature<Geometry> | undefined;
        const geometry = feature?.getGeometry();
        const extent =
          Array.isArray(record.extent) && record.extent.length === 4
            ? (record.extent as Extent)
            : geometry?.getExtent();

        if (!feature || !geometry || !extent) {
          return;
        }

        // @ts-ignore
        const commentsSource = new OlSourceVector({features: [feature] as OlFeature<Geometry>});
        setSelectedSource(commentsSource);

        const v = mapContext.map.getView();
        v.fit(extent, {
          padding: [20, 20, 20, 220],
          duration: 1000,
          maxZoom: 19,
        });
      }
    }
  }

  const handleOpenSettings = (evt: React.MouseEvent<HTMLButtonElement>) => {
    setSettingsAnchorEl(evt.currentTarget);
  };

  const handleCloseSettings = () => {
    setSettingsAnchorEl(null);
  };

  const handleToggleShowActiveOnly = (checked: boolean) => {
    setShowActiveOnly(checked);
    handleCloseSettings();
  };

  const settingsControl = (
    <>
      <IconButton size="small" onClick={handleOpenSettings}>
        <SettingsIcon />
      </IconButton>
      <Menu
        open={Boolean(settingsAnchorEl)}
        onClose={handleCloseSettings}
        anchorEl={settingsAnchorEl}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
      >
        <MenuItem disableRipple>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={showActiveOnly}
                onChange={(evt) => handleToggleShowActiveOnly(evt.target.checked)}
              />
            }
            label={
              <Typography
                variant="caption"
                style={{
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
              >
                {t('comments.show_active_only')}
              </Typography>
            }
          />
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <Box
      sx={{
        height: '88%',
      }}
    >
      {/* <FormControlLabel
        control={
          <Checkbox
            value={visible}
            color="primary"
            onChange={() => {
              toggleVisibility ? toggleVisibility() : null;
            }}
          />
        }
        label={t('comments.view')}
      /> */}
      <ModelTable
        dc={dc}
        records={filteredRecords}
        allowSelection="none"
        title=""
        allowRowAction={() => true}
        onRowAction={handleRowAction}
        viewName="pane"
        allowColumnPicker
        customHeaderControl={settingsControl}
      />
      <Box mt={2}>
        <TableButton
          variant="contained"
          allow="always"
          onClick={handleAddComment}
          startIcon={<AddIcon />}
          selection={{}}
        >
          {t('titles.add_comment')}
        </TableButton>
      </Box>
    </Box>
  );
};

export default CommentsPane;
